import { createHeadlessEditor } from "@lexical/headless";
import { $generateHtmlFromNodes } from "@lexical/html";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createIsomorphicFn } from "@tanstack/react-start";
import type { SerializedEditorState } from "lexical";
import { type ReactNode, Suspense } from "react";

import type { LexicalState } from "@/db/schema/shared";

import { EDITOR_CONFIG_DEFAULTS } from "./config";
import { RichTextEditorModal } from "./modal";

export type RichTextDisplayProps = {
  name: string;
  content?: LexicalState | null;
  onSave?: (state: LexicalState) => Promise<void>;
  footer?: ReactNode;
};

async function setupDom() {
  const { parseHTML } = await import("linkedom");
  const { window, document } = parseHTML(
    "<!DOCTYPE html><html><body></body></html>",
  );

  const _window = global.window;
  const _document = global.document;

  // @ts-expect-error linkedom global assignment
  global.window = window;
  global.document = document;

  return () => {
    global.window = _window;
    global.document = _document;
  };
}

const generateHtml = createIsomorphicFn()
  // const generateHtml = createServerFn()
  // .inputValidator(({ name, state }: { name: string; state: string }) => ({
  //   name,
  //   state,
  // }))
  // .handler(async ({ data: { name, state } }) => {
  .server(async (name: string, state: string) => {
    const cleanup = await setupDom();

    return await new Promise<string>((resolve) => {
      const editor = createHeadlessEditor({
        ...EDITOR_CONFIG_DEFAULTS,
        namespace: name,
        onError(error: Error) {
          throw error;
        },
      });

      editor.setEditorState(editor.parseEditorState(state));

      editor.update(() => {
        try {
          const _html = $generateHtmlFromNodes(editor, null);
          cleanup();

          resolve(_html);
        } catch (e) {
          console.log(e);
        }
      });
    });
  })
  .client(async (name: string, state: string) => {
    return await new Promise<string>((resolve) => {
      const editor = createHeadlessEditor({
        ...EDITOR_CONFIG_DEFAULTS,
        namespace: name,
        onError(error: Error) {
          throw error;
        },
      });

      editor.setEditorState(editor.parseEditorState(state));

      editor.update(() => {
        try {
          const _html = $generateHtmlFromNodes(editor, null);

          resolve(_html);
        } catch (e) {
          console.log(e);
        }
      });
    });
  });

export function RichTextDisplay({
  name,
  content,
  onSave,
  footer,
}: RichTextDisplayProps) {
  const hasContent =
    content != null && (content.root?.children?.length ?? 0) > 0;
  const state = hasContent ? JSON.stringify(content) : null;

  const { data: html } = useSuspenseQuery({
    queryKey: ["rich-text-html", state],
    queryFn: () => (state ? generateHtml(name, state) : ""),
    staleTime: state ? undefined : Number.POSITIVE_INFINITY,
  });

  return (
    <Suspense>
      <div className="relative flex-1">
        {html && (
          // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized Lexical output
          <div dangerouslySetInnerHTML={{ __html: html }} />
        )}

        {onSave && (
          <RichTextEditorModal
            triggerClassName="absolute top-0 right-0 -translate-y-full"
            name={name}
            title="Update Page Content"
            initialValue={content as SerializedEditorState | undefined}
            onSave={async (state) => {
              await onSave(state as LexicalState);
            }}
            footer={footer}
          />
        )}
      </div>
    </Suspense>
  );
}
